import type { Editor } from "@tiptap/react";
import { applyDiffToEditor } from "@/components/tiptap/customExtensions/aiChange/applyDifftoEditor";
import { parseSSEChunk } from "@/hooks/generate/parseSSE";

function parseMarkdownToHTML(markdown: string): string {
  
  const lines = markdown.split('\n');
  const htmlParts: string[] = [];
  
  for (let line of lines) {
    line = line.trim();
    
    // 빈 줄은 건너뛰기
    if (!line) {
      continue;
    }
    
    // 구분선 처리 (---, ***, ___, 또는 3개 이상의 대시/별표/언더스코어)
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
      htmlParts.push('<hr>');
      continue;
    }
    
    // 헤딩 처리
    if (line.startsWith('### ')) {
      const text = line.substring(4).trim();
      htmlParts.push(`<h3>${text}</h3>`);
    } else if (line.startsWith('## ')) {
      const text = line.substring(3).trim();
      htmlParts.push(`<h2>${text}</h2>`);
    } else if (line.startsWith('# ')) {
      const text = line.substring(2).trim();
      htmlParts.push(`<h1>${text}</h1>`);
    } 
    // 리스트 처리
    else if (line.startsWith('- ') || line.startsWith('* ') || line.startsWith('+ ')) {
      const text = line.substring(2).trim();
      const processedText = text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>');
      htmlParts.push(`<ul><li>${processedText}</li></ul>`);
    }
    // 숫자 리스트 처리
    else if (/^\d+\.\s/.test(line)) {
      const text = line.replace(/^\d+\.\s/, '').trim();
      const processedText = text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>');
      htmlParts.push(`<ol><li>${processedText}</li></ol>`);
    }
    else {
      // 일반 텍스트 처리
      const processedText = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>');
      
      htmlParts.push(`<p>${processedText}</p>`);
    }
  }
  
  const result = htmlParts.join('');
  return result;
}

export async function streamGeneration(
  action:
    | "summarize"
    | "enrich"
    | "translate"
    | "checkGrammar"
    | "customPrompt",
  input: string,
  editor: Editor,
  langCode?: string,
  loadingStartPos?: number | null,
  loadingText?: string
): Promise<void> {
  const token = localStorage.getItem("access_token");
  if (!token) return;

  const url = `${import.meta.env.VITE_API_URL}/api/v1/generate/stream`;
  const body = JSON.stringify({ action, input_text: input, langCode });

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body,
  });

  if (!res.body) {
    return;
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();

  editor.commands.focus();
  const startPos = editor.state.selection.from;
  const endPos = editor.state.selection.to;
  let finalResult = "";
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    if (!value) continue;

    buffer += value;

    const parts = buffer.split(/\r?\n\r?\n/);
    buffer = parts.pop() ?? "";

    for (const chunk of parts) {
      const messages = parseSSEChunk(chunk);

      for (const msg of messages) {
        if (msg.data?.trim() === "[done]") {
          editor.commands.unsetMark("streaming");

          if (
            ["summarize", "enrich", "translate", "checkGrammar"].includes(
              action
            )
          ) {
            editor.commands.deleteRange({ from: startPos, to: endPos });
            applyDiffToEditor({
              editor,
              original: input,
              updated: finalResult,
              action
            });
          } else {
            
            try {
              // 마크다운을 HTML로 변환
              const htmlContent = parseMarkdownToHTML(finalResult);

              
              // 스트리밍이 완전히 끝난 후 로딩 텍스트 제거
              if (loadingStartPos !== null && loadingStartPos !== undefined && loadingText) {
                const endPos = loadingStartPos + loadingText.length;
                editor.commands.deleteRange({ from: loadingStartPos, to: endPos });
                editor.commands.setTextSelection(loadingStartPos);
              }
              
              // HTML 콘텐츠 삽입
              editor.commands.insertContent(htmlContent);
              
            } catch (error) {
              
              // 에러 발생 시에도 로딩 텍스트 제거
              if (loadingStartPos !== null && loadingStartPos !== undefined && loadingText) {
                const endPos = loadingStartPos + loadingText.length;
                editor.commands.deleteRange({ from: loadingStartPos, to: endPos });
                editor.commands.setTextSelection(loadingStartPos);
              }
              
              // 실패 시 원본 텍스트로 삽입
              editor.commands.insertContent(`<p>${finalResult}</p>`);
            }
          }

          return;
        }

        const content = msg.data ?? "";

        if (
          ["summarize", "enrich", "translate", "checkGrammar"].includes(action)
        ) {
          finalResult += content;
        } else {
          finalResult += content;
          // 스트리밍 중에는 로딩 텍스트를 유지
        }
      }
    }
  }
}